import Game from '../../Game';
import IPlayerData from '../../Player/IPlayerData';
import { setSentryUserID } from '../../Sentry';
import { ICustomEntryPoint } from '../../State/Game/EntryPoint/GameEntryPoint';
import { IAPCatalog } from '../../State/Game/IAP';
import { IGameState } from '../../State/Game/IGameState';
import { IPlayerState } from '../../State/Player/IPlayerState';
import { getCompletedLevels } from '../../State/Player/PlayerLevelHelper';
import LeaderboardHelper from '../../System/UI/LeaderboardHelper';
import DateHelper from '../DateHelper';
import { FBTrackingEvent } from '../Tracking/FBTrackingEvent';
import { track } from '../Tracking/Track';
import { IAdvertisementPlacement } from './Advertisement';
import ISDKDriver from './ISDKDriver';
import { LanguageKey } from './Language';
import { getLevelDataBasedFromScores as getLevelDataBasedOnScores } from './LevelDataRestoreHelper';

export default class SDKManager {
    public driver: ISDKDriver;

    public async init() {
        this.driver = new Game.config.SDKDriver();
        return this.driver.init(this);
    }

    public async loadingProgress(progress: number) {
        return this.driver.loadingProgress(progress);
    }

    public async loadingComplete() {
        await this.driver.loadComplete();

        // Set Sentry context once available.
        setSentryUserID(this.getUserId());

        track('userID', this.getUserId());
        track('userLocale', this.getLanguage());
    }

    public async save(data: Partial<IPlayerData>) {
        return this.driver.save(data);
    }

    public async flushSaveData() {
        return this.driver.flushSaveData();
    }

    public async restore() {
        return this.driver.restore();
    }

    public async playWithFriends(
        text: string,
        imageBase64: string,
        payload: ICustomEntryPoint
    ) {
        return this.driver.playWithFriends(text, imageBase64, payload);
    }

    public async customMessage(
        text: string,
        imageBase64: string,
        payload: ICustomEntryPoint,
        strategy?: string,
        cta?: string
    ) {
        return this.driver.customMessage(
            text,
            imageBase64,
            payload,
            strategy,
            cta
        );
    }

    public async createContext(playerId: string) {
        return this.driver.createContext(playerId);
    }

    public async purchaseProduct(productId: string) {
        return this.driver.purchaseProduct(productId);
    }

    public async resolveUnconsumedPurchases() {
        return this.driver.resolveUnconsumedPurchases();
    }

    public getEntryPoint(): any {
        return this.driver.getEntryPoint();
    }

    public getAvatar(): string {
        return this.driver.getAvatar();
    }

    public getUserId(): string {
        return this.driver.getUserId();
    }

    public getUsername(): string {
        return this.driver.getUsername();
    }

    public getServerTime(): Promise<string> {
        return this.driver.getServerTime();
    }

    public async fetchCatalog() {
        return this.driver.fetchCatalog();
    }

    public getCatalog(): IAPCatalog {
        return this.driver.getCatalog();
    }

    public async share(
        text: string,
        imageBase64: string,
        payload?: ICustomEntryPoint,
        intent?: string
    ) {
        return this.driver.share(text, imageBase64, payload, intent);
    }

    public async postScore(
        leaderboardName: string,
        instanceId: string,
        score: number
    ) {
        return this.driver.postScore(leaderboardName, instanceId, score);
    }

    public async getFriendScores(leaderboardName: string, instanceId: string) {
        return this.driver.getFriendsScores(leaderboardName, instanceId);
    }

    public async clearPlayerData() {
        return this.driver.clearPlayerData();
    }

    public async displayAdvertisement(
        placement: IAdvertisementPlacement,
        state: IGameState,
        payload?: any
    ) {
        return this.driver.displayAdvertisement(placement, state, payload);
    }

    public async levelStart(levelNumber: number) {
        return this.driver.levelStart(levelNumber);
    }

    public async levelFinish(state: 'win' | 'lose') {
        if (!this.driver.levelFinish) return;

        return this.driver.levelFinish(state);
    }

    public async canSubscribeBot() {
        return this.driver.canSubscribeBot();
    }

    public async subscribeBot() {
        return this.driver.subscribeBot();
    }

    public async canCreateShortcut() {
        return this.driver.canCreateShortcut();
    }

    public async createShortcut() {
        return this.driver.createShortcut();
    }

    /**
     * Platform supports switching to a different game (i.e. cross promo link).
     */
    public canSwitchGame() {
        return this.driver.canSwitchGame();
    }

    public async gameTracking(
        event: FBTrackingEvent,
        value: string | number = null,
        parameters: { [key: string]: string } = null
    ) {
        if (parameters === null) {
            parameters = {};
        }

        // Add standard 'user_type' parameter to all events.
        if (Game.player) {
            parameters.user_type = Game.player.FTUE
                ? 'new_user'
                : 'existing_user';
        }

        return this.driver.gameTracking(event, value, parameters);
    }

    public getLanguage(): LanguageKey {
        return this.driver.getLanguage();
    }

    /**
     * Check for backup. Create a backup if none exists.
     * This backup is one-time for upgrade 1.x.x to 2.0.x
     * @param playerData
     */
    public async prepareMigrationBackup(playerData: IPlayerData) {
        const backup = playerData.data_backup;

        // tslint:disable-next-line:no-console
        console.log('Checking if backup exists...');

        if (backup && typeof backup === 'string') {
            try {
                const data = JSON.parse(backup);
                if (
                    data !== null &&
                    typeof data === 'object' &&
                    data.root !== null &&
                    typeof data.root === 'object' &&
                    data.root.version // Must have a version - this will replace backups from versions prior to v2.1.2.
                ) {
                    // tslint:disable-next-line:no-console
                    console.log('Backup found. Nothing to do.');
                    return;
                }
            } catch (e) {
                // tslint:disable-next-line:no-console
                console.warn('Failed to parse backup JSON.', e);
            }
        }

        // tslint:disable-next-line:no-console
        console.log('No backup found. Creating backup...');

        const newBackupData = JSON.stringify({
            root: playerData,
            backup_data_timestamp: DateHelper.now().toString(),
        });

        await this.save({ data_backup: newBackupData });

        // tslint:disable-next-line:no-console
        console.log('Backup created.');
    }

    public hasIAPSupport(): boolean {
        return this.driver.hasIAPSupport();
    }

    public hasCrossPromoSupport(): boolean {
        return this.driver.hasCrossPromoSupport();
    }

    public hasRewardedAdSupport(): boolean {
        return this.driver.hasRewardedAdSupport();
    }

    public hasInterstitialAdSupport(): boolean {
        return this.driver.hasInterstitialAdSupport();
    }

    /**
     * There is a potential issue when user coming from SOLI (1.x.x) may brings
     * save data that has discrepancies between its stars and level progress.
     * This function should handle this pushing player progress data to the last
     * level played based on leaderboard.
     */
    public async restoreLostPlayerProgress(
        playerState: IPlayerState
    ): Promise<IPlayerState> {
        const leaderboardLevel: number = await LeaderboardHelper.getLatestCompletedLevel();

        // Get player data to compare levels completed with the latest one from Leaderboard.
        const currentLevel: number = getCompletedLevels(playerState.levels.saga)
            .length;

        // Compare if the level on Leaderboard is higher than the mostly recent completed level.
        if (leaderboardLevel > currentLevel) {
            const userScores: number[] = await this.driver.getLeaderboardPlayerScores();

            if (!userScores || userScores.length === 0) {
                return playerState;
            }

            playerState.levels = {
                ...playerState.levels,
                saga: getLevelDataBasedOnScores(
                    userScores,
                    Game.resource.get('level_stars')
                ),
            };

            if (
                playerState.dataRestoreReward === 1 &&
                playerState.dataRestoreCounter === 0
            ) {
                playerState.dataRestoreCounter = 1;
            }

            if (playerState.dataRestoreCounter === 0) {
                this.gameTracking(FBTrackingEvent.ProgressRestore_Initial);
            } else {
                this.gameTracking(FBTrackingEvent.ProgressRestore_Other);
            }

            playerState.dataRestoreCounter += 1;

            return playerState;
        }

        return playerState;
    }

    public handleStateChanged(state: IGameState) {}

    public async switchGame(appID: string, placement: string) {
        return this.driver.switchGame(appID, placement);
    }

    public async isGBCConnected() {
        return this.driver.isGBCConnected();
    }

    public async waitForPaymentsEnabled() {
        return this.driver.waitForPaymentsEnabled();
    }

    public async getCrossPromo() {
        return this.driver.getCrossPromoData();
    }
}
